/* eslint-disable max-len */
import { FormattedMessage, useIntl } from 'react-intl';
import {
    ClipboardListIcon,
    CogIcon,
    CreditCardIcon,
    DatabaseIcon,
    FolderIcon,
    HomeIcon,
    LockClosedIcon,
    OfficeBuildingIcon,
    PhotographIcon,
    PlusCircleIcon,
    QuestionMarkCircleIcon,
    SearchIcon,
    ShoppingCartIcon,
    SupportIcon,
    ExclamationCircleIcon,
    MailIcon,
    UserIcon,
    CodeIcon,
} from '@heroicons/react/solid';
import env from 'src/env/index';
import { useNavigate } from 'react-router-dom';
import { SideMenu } from '@luminalearning/components';
import supportURL from 'src/utils/supportUrl';
import { useQuery, useReactiveVar } from '@apollo/client';
import { activePractitionerDetails } from 'src/graphql';
import { loginLuminaOnline } from 'src/services/auth';
import { useState } from 'react';
import GET_LOCALE from 'src/graphql/queries/intlQuery';

type Props = {
    isMobile?: boolean;
    isApiPractitioner?: boolean;
};

const Sidebar = (props: Props) => {
    const { isMobile, isApiPractitioner } = props;
    const intl = useIntl();
    const { data: locale } = useQuery(GET_LOCALE);
    const practitionerDetails = useReactiveVar(activePractitionerDetails);
    const [isNavigating, setIsNavigating] = useState(false);
    const navigate = useNavigate();

    const handleLuminaOnlineNavigation = async (targetPath: string) => {
        if (practitionerDetails && !isNavigating) {
            setIsNavigating(true);
            await loginLuminaOnline(practitionerDetails.id);
            window.location.href = `${env.luminaOnlineUrl}/${targetPath}`;
            setIsNavigating(false);
        }
    };

    const handleLuminaFinanceNavigation = (targetPath: string) => {
        if (practitionerDetails && !isNavigating) {
            setIsNavigating(true);
            window.location.href = `${env.luminaFinanceUrl}/login-lumina-finance/affiliate/${practitionerDetails.id}?targetUrl=${env.luminaFinanceUrl}/${targetPath}`;
            setIsNavigating(false);
        }
    };

    const sidebarMenu = [{
        title: intl.formatMessage({ id: 'LP_PROJECTS', defaultMessage: 'Projects' }),
        contents: [{
            icon: PlusCircleIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliateprojects'),
            isActive: false,
            title: <FormattedMessage id='LP_CREATE_NEW_PROJECT' defaultMessage='Create New Project'/>,
        }, {
            icon: CogIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliateprojects'),
            isActive: false,
            title: <FormattedMessage id='LP_MANAGE_PROJECT' defaultMessage='Manage Projects'/>,
        }, {
            icon: OfficeBuildingIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliateorganisations'),
            isActive: false,
            title: <FormattedMessage id='LP_ORGANISATIONS' defaultMessage='Organisations'/>,
        }, {
            icon: SearchIcon,
            onClick: () => handleLuminaOnlineNavigation('affglobalusersearch'),
            isActive: false,
            title: <FormattedMessage id='LP_SEARCH_PARTICIPANTS' defaultMessage='Search Participants'/>,
        }, {
            icon: MailIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliatemails'),
            isActive: false,
            title: <FormattedMessage id='LP_EMAILS' defaultMessage='Emails'/>,
        }],
    }, {
        title: intl.formatMessage({ id: 'LP_STORE', defaultMessage: 'Lumina Store' }),
        contents: [{
            icon: ShoppingCartIcon,
            onClick: () => handleLuminaFinanceNavigation('practitioner/store'),
            isActive: false,
            title: <FormattedMessage id='LP_NEW_ORDER' defaultMessage='New Order'/>,
        }, {
            icon: ClipboardListIcon,
            onClick: () => handleLuminaFinanceNavigation('practitioner/orders'),
            isActive: false,
            title: <FormattedMessage id='LP_ORDER_HISTORY' defaultMessage='Order History'/>,
        }, {
            icon: DatabaseIcon,
            onClick: () => handleLuminaFinanceNavigation('practitioner/pointsburned'),
            isActive: false,
            title: <FormattedMessage id='LP_POINTS_HISTORY' defaultMessage='Points History'/>,
        }, {
            icon: CreditCardIcon,
            onClick: () => handleLuminaFinanceNavigation('practitioner/billingandtax'),
            isActive: false,
            title: <FormattedMessage id='LP_BILLING_ADRESS_DETAILS' defaultMessage='Billing Address Details'/>,
        }],
    }, {
        title: intl.formatMessage({ id: 'LP_SUPPORT', defaultMessage: 'Support' }),
        contents: [{
            icon: FolderIcon,
            onClick: () => navigate('/resources'),
            isActive: window.location.pathname === '/resources',
            title: <FormattedMessage id='LP_RESOURCE_LIBRARY' defaultMessage='Resources'/>,
        }, {
            icon: QuestionMarkCircleIcon,
            target: '_blank',
            isActive: false,
            href: supportURL(locale.appLocale),
            title: <FormattedMessage id='LP_KNOWLEDGE_BASE' defaultMessage='Knowledge Base'/>,
        }, {
            icon: SupportIcon,
            onClick: () => navigate('/support'),
            isActive: window.location.pathname === '/support',
            title: <FormattedMessage id='LP_SUPPORT_DESK' defaultMessage='Support Desk'/>,
        }],
    }, {
        title: intl.formatMessage({ id: 'LP_SETTINGS', defaultMessage: 'Settings' }),
        contents: [{
            icon: UserIcon,
            isActive: false,
            onClick: () => handleLuminaOnlineNavigation('affiliatepersonal'),
            title: <FormattedMessage id='LP_PRACTITIONER_DETAILS' defaultMessage='Practitioner Details'/>,
        }, {
            icon: PhotographIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliateimagery'),
            isActive: false,
            title: <FormattedMessage id='LP_YOUR_IMAGERY' defaultMessage='Your Imagery'/>,
        }, {
            icon: LockClosedIcon,
            onClick: () => handleLuminaOnlineNavigation('practitionerprivacysettings'),
            isActive: false,
            title: <FormattedMessage id='LP_PRIVACY_SETTINGS' defaultMessage='Privacy Settings'/>,
        }, {
            icon: ExclamationCircleIcon,
            onClick: () => handleLuminaOnlineNavigation('affiliatealerts'),
            isActive: false,
            title: <FormattedMessage id='LP_POINTS_ALERT' defaultMessage='Points Level Alert'/>,
        }, (
            isApiPractitioner
                ? {
                    icon: CodeIcon,
                    isActive: window.location.pathname === '/developer' || window.location.pathname === '/developer/documentation',
                    onClick: () => navigate('/developer'),
                    title: <FormattedMessage id='LP_DEVELOPER_DASHBOARD' defaultMessage='Developer Dashboard'/>,
                } : {}
        )],
    }];

    if (!practitionerDetails) {
        return <></>;
    }

    return (
        <div className={`overflow-y-auto w-64 xl:w-64 ${isMobile ? 'mt-40 w-80 h-full' : ''}`}>
            <SideMenu.LinkItem
                icon={HomeIcon}
                onClick={() => { navigate('/'); }}
                size="Large"
                className='mt-6 ml-7 mr-6'
            >
                <p className={`text-white ${window.location.pathname === '/' ? 'underline underline-2' : ''}`} >
                    {
                        intl.formatMessage({
                            id: 'LP_DASHBOARD_TITLE',
                            defaultMessage: 'Practitioner Dashboard',
                        })
                    }
                </p>
            </SideMenu.LinkItem>
            { sidebarMenu.map((categoryMenu) => (
                <SideMenu.Category categoryName={categoryMenu.title} key={categoryMenu.title}>
                    { categoryMenu.contents.map((menu, idx) => {
                        const { title, isActive, ...menuProps } = menu;
                        return <SideMenu.LinkItem { ...menuProps } key={idx} className='cursor-pointer'>
                            <p className={`text-white ${isActive ? 'border-b' : ''}`}>{title}</p>
                        </SideMenu.LinkItem>;
                    }) }
                </SideMenu.Category>
            )) }
        </div>
    );
};

export default Sidebar;